import { useState } from "react";
import { useTranslation } from "react-i18next";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Alert from "@cloudscape-design/components/alert";
import Link from "@cloudscape-design/components/link";
import Box from "@cloudscape-design/components/box";
import { useAuth } from "../contexts/AuthContext";
import "./LoginView.css";

function LoginView({ onNavigate }) {
  const { t } = useTranslation();
  const { signIn, forgotPassword, confirmResetPassword, error, clearError, midwaySignIn, isMidwayLoading } = useAuth();

  const [view, setView] = useState("login"); // login, forgot, resetConfirm
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState("");

  const clearForm = () => {
    setPassword("");
    setConfirmPassword("");
    setVerificationCode("");
    setLocalError("");
    clearError();
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setLocalError("");
    const result = await signIn(email, password);
    setIsLoading(false);
    if (!result.success) setLocalError(result.error);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setLocalError(t("auth.login.enter-email-first"));
      return;
    }
    setIsLoading(true);
    setLocalError("");
    const result = await forgotPassword(email);
    setIsLoading(false);
    if (result.success) {
      setView("resetConfirm");
      clearForm();
    } else {
      setLocalError(result.error);
    }
  };

  const handleResetConfirm = async () => {
    if (password !== confirmPassword) {
      setLocalError(t("auth.login.passwords-no-match"));
      return;
    }
    setIsLoading(true);
    setLocalError("");
    const result = await confirmResetPassword(email, verificationCode, password);
    setIsLoading(false);
    if (result.success) {
      setView("login");
      clearForm();
    } else {
      setLocalError(result.error);
    }
  };

  const displayError = localError || error;

  const renderLogin = () => (
    <SpaceBetween size="l">
      {displayError && <Alert type="error" dismissible onDismiss={() => { setLocalError(""); clearError(); }}>{displayError}</Alert>}
      <Button variant="primary" loading={isMidwayLoading} onClick={midwaySignIn} fullWidth>
        {t("auth.login.sign-in-midway")}
      </Button>
      <Box textAlign="center" color="text-body-secondary">
        <span className="login-divider">{t("auth.login.or-divider")}</span>
      </Box>
      <FormField label={t("auth.login.email")}>
        <Input type="email" value={email} onChange={({ detail }) => setEmail(detail.value)} placeholder="user@amazon.com" />
      </FormField>
      <FormField label={t("auth.login.password")}>
        <Input type="password" value={password} onChange={({ detail }) => setPassword(detail.value)} />
      </FormField>
      <Button variant="primary" loading={isLoading} onClick={handleLogin} fullWidth>{t("auth.login.sign-in")}</Button>
      <Box textAlign="center">
        <Link onFollow={() => { clearForm(); setView("forgot"); }}>{t("auth.login.forgot-password")}</Link>
      </Box>
    </SpaceBetween>
  );

  const renderForgot = () => (
    <SpaceBetween size="l">
      {displayError && <Alert type="error" dismissible onDismiss={() => { setLocalError(""); clearError(); }}>{displayError}</Alert>}
      <FormField label={t("auth.login.email")}>
        <Input type="email" value={email} onChange={({ detail }) => setEmail(detail.value)} placeholder="user@amazon.com" />
      </FormField>
      <Button variant="primary" loading={isLoading} onClick={handleForgotPassword} fullWidth>{t("auth.login.send-reset-code")}</Button>
      <Box textAlign="center">
        <Link onFollow={() => { clearForm(); setView("login"); }}>{t("auth.login.back-to-login")}</Link>
      </Box>
    </SpaceBetween>
  );

  const renderResetConfirm = () => (
    <SpaceBetween size="l">
      {displayError && <Alert type="error" dismissible onDismiss={() => { setLocalError(""); clearError(); }}>{displayError}</Alert>}
      <Alert type="info">{t("auth.login.reset-code-sent", { email })}</Alert>
      <FormField label={t("auth.login.verification-code")}>
        <Input value={verificationCode} onChange={({ detail }) => setVerificationCode(detail.value)} placeholder="123456" />
      </FormField>
      <FormField label={t("auth.login.new-password")}>
        <Input type="password" value={password} onChange={({ detail }) => setPassword(detail.value)} />
      </FormField>
      <FormField label={t("auth.login.confirm-password")}>
        <Input type="password" value={confirmPassword} onChange={({ detail }) => setConfirmPassword(detail.value)} />
      </FormField>
      <Button variant="primary" loading={isLoading} onClick={handleResetConfirm} fullWidth>{t("auth.login.reset-password")}</Button>
      <Box textAlign="center">
        <Link onFollow={() => { clearForm(); setView("login"); }}>{t("auth.login.back-to-login")}</Link>
      </Box>
    </SpaceBetween>
  );

  const titles = {
    login: t("auth.login.sign-in"),
    forgot: t("auth.login.forgot-password"),
    resetConfirm: t("auth.login.reset-password"),
  };

  return (
    <div className="login-container">
      <div className="login-branding">
        <img src="/amazon-nova-sonic-logo.svg" alt="Amazon Nova Sonic" className="login-logo" />
        <span className="login-portal-text">{t("app.common.demos-portal")}</span>
      </div>
      <Container header={<Header variant="h1">{titles[view]}</Header>}>
        {view === "login" && renderLogin()}
        {view === "forgot" && renderForgot()}
        {view === "resetConfirm" && renderResetConfirm()}
      </Container>
    </div>
  );
}

export default LoginView;
